import React, { useState, useEffect } from "react";
import "../../styles/adminvideos.css"; // Créez et adaptez ce fichier CSS pour le style
import apiClient from "../../Api/apiConfig"; // Assurez-vous que apiClient est bien configuré

const AdminVideos = () => {
  // État pour stocker la liste des vidéos
  const [videos, setVideos] = useState([]);
  // État pour contrôler l'affichage de la modale
  const [showModal, setShowModal] = useState(false);
  // Mode de la modale : "add" (ajouter) ou "edit" (modifier)
  const [modalMode, setModalMode] = useState("add");
  // État pour le formulaire (pour ajouter ou modifier une vidéo)
  const [formData, setFormData] = useState({
    id: "",
    title: "",
    description: "",
    youtubeVideoId: "",
    thumbnailUrl: "",
    status: "active",
  });

  // Récupérer les vidéos au chargement du composant
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        // Supposons que apiClient est une instance Axios avec une baseURL définie
        const response = await apiClient.get("/api/videos");
        setVideos(response.data);
      } catch (error) {
        console.error("Erreur lors de la récupération des vidéos :", error);
      }
    };

    fetchVideos();
  }, []);

  // Ouvre la modale en mode "ajouter"
  const openAddModal = () => {
    setModalMode("add");
    setFormData({
      id: "",
      title: "",
      description: "",
      youtubeVideoId: "",
      thumbnailUrl: "",
      status: "active",
    });
    setShowModal(true);
  };

  // Ouvre la modale en mode "modifier" avec les données de la vidéo sélectionnée
  const openEditModal = (video) => {
    setModalMode("edit");
    setFormData({
      id: video.id,
      title: video.title,
      description: video.description,
      youtubeVideoId: video.youtubeVideoId,
      thumbnailUrl: video.thumbnailUrl,
      status: video.status,
    });
    setShowModal(true);
  };

  // Ferme la modale
  const closeModal = () => {
    setShowModal(false);
  };

  // Mise à jour des valeurs du formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Gère la soumission du formulaire (ajout ou modification)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (modalMode === "add") {
      // Créer une nouvelle vidéo
      try {
        const res = await fetch("https://api.nms06.com/api/videos", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: formData.title,
            description: formData.description,
            youtubeVideoId: formData.youtubeVideoId,
            thumbnailUrl: formData.thumbnailUrl,
            status: formData.status,
          }),
        });
        if (!res.ok) {
          throw new Error("Erreur lors de l'ajout de la vidéo");
        }
        const newVideo = await res.json();
        setVideos((prev) => [...prev, newVideo]);
        closeModal();
      } catch (error) {
        console.error(error);
      }
    } else if (modalMode === "edit") {
      // Mettre à jour une vidéo existante
      try {
        const res = await fetch(
          `https://api.nms06.com/api/videos/${formData.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              title: formData.title,
              description: formData.description,
              youtubeVideoId: formData.youtubeVideoId,
              thumbnailUrl: formData.thumbnailUrl,
              status: formData.status,
            }),
          }
        );
        if (!res.ok) {
          throw new Error("Erreur lors de la mise à jour de la vidéo");
        }
        const updatedVideo = await res.json();
        setVideos((prev) =>
          prev.map((video) =>
            video.id === updatedVideo.id ? updatedVideo : video
          )
        );
        closeModal();
      } catch (error) {
        console.error(error);
      }
    }
  };

  // Gère la suppression d'une vidéo avec confirmation
  const handleDelete = async (id) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette vidéo ?")) {
      try {
        const res = await fetch(`https://api.nms06.com/api/videos/${id}`, {
          method: "DELETE",
        });
        if (!res.ok) {
          throw new Error("Erreur lors de la suppression de la vidéo");
        }
        setVideos((prev) => prev.filter((video) => video.id !== id));
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <div className="admin-videos">
      <h1>Administration des vidéos</h1>
      <button onClick={openAddModal} className="add-button">
        Ajouter une vidéo
      </button>

      {/* Tableau de présentation des vidéos */}
      <table className="videos-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Titre</th>
            <th>Description</th>
            <th>ID YouTube</th>
            <th>Miniature</th>
            <th>Statut</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {videos.map((video) => (
            <tr key={video.id}>
              <td>{video.id}</td>
              <td>{video.title}</td>
              <td>{video.description}</td>
              <td>{video.youtubeVideoId}</td>
              <td>
                {video.thumbnailUrl && (
                  <img
                    src={video.thumbnailUrl}
                    alt={video.title}
                    style={{ width: "100px" }}
                  />
                )}
              </td>
              <td>{video.status}</td>
              <td>
                <button onClick={() => openEditModal(video)}>Modifier</button>
                <button onClick={() => handleDelete(video.id)}>
                  Supprimer
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Modal pour ajouter/modifier une vidéo */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={closeModal}>
              &times;
            </button>
            <h2>
              {modalMode === "add"
                ? "Ajouter une vidéo"
                : "Modifier la vidéo"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Titre</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                ></textarea>
              </div>
              <div className="form-group">
                <label>ID YouTube</label>
                <input
                  type="text"
                  name="youtubeVideoId"
                  value={formData.youtubeVideoId}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>URL de la miniature</label>
                <input
                  type="text"
                  name="thumbnailUrl"
                  value={formData.thumbnailUrl}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Statut</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <button type="submit">
                {modalMode === "add" ? "Ajouter" : "Modifier"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVideos;
